import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { Download, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUpgradeStore } from '../../stores/upgradeStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkConfirmRejectModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<{
    processed: number;
    succeeded: number;
    failed: number;
    errors: any[];
  } | null>(null);

  const { bulkConfirmReject } = useUpgradeStore();

  const handleDownloadTemplate = () => {
    // Create template data
    const template = [
      {
        passport_number: '12345678',
        hotel_name: 'Grand Plaza Hotel',
        status: 'confirm'
      },
      {
        passport_number: '87654321',
        hotel_name: 'Grand Plaza Hotel',
        status: 'reject',
        reason: 'Optional rejection reason'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    
    // Add headers with instructions
    XLSX.utils.sheet_add_aoa(ws, [[
      'Passport Number',
      'Hotel Name',
      'Status (confirm or reject)',
      'Reason (only for reject)'
    ]], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Passport Number
      { wch: 30 }, // Hotel Name
      { wch: 15 }, // Status
      { wch: 30 }, // Reason
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-confirm-reject-template.xlsx');
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsProcessing(true);
    setValidationErrors([]);
    setResults(null);

    try {
      // Read the Excel file
      const reader = new FileReader();
      const fileData = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(e);
        reader.readAsBinaryString(file);
      });

      // Parse the Excel data
      const workbook = XLSX.read(fileData, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet);

      // Validate rows
      const errors: string[] = [];
      const confirmations: Array<{
        passportNumber: string; 
        hotel: string; 
        status: 'confirm' | 'reject';
        reason?: string;
      }> = [];

      rows.forEach((row: any, index) => {
        // Extract values with flexible column naming
        const passport = row.passport_number || row.passport || row['Passport Number'];
        const hotel = row.hotel_name || row.hotel || row['Hotel Name'];
        const status = row.status || row.Status;
        const reason = row.reason || row.Reason;
        
        // Validate required fields
        if (!passport) {
          errors.push(`Row ${index + 1}: Missing passport number`);
        }
        
        if (!hotel) {
          errors.push(`Row ${index + 1}: Missing hotel name`);
        }
        
        if (!status || !['confirm', 'reject'].includes(status.toLowerCase())) {
          errors.push(`Row ${index + 1}: Status must be either 'confirm' or 'reject'`);
        } 
        
        // For valid rows, add to confirmations
        if (passport && hotel && status && ['confirm', 'reject'].includes(status.toLowerCase())) {
          confirmations.push({
            passportNumber: passport.toString(),
            hotel: hotel.toString(),
            status: status.toLowerCase() as 'confirm' | 'reject',
            reason: status.toLowerCase() === 'reject' && reason ? reason.toString() : undefined
          });
        }
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
        setIsProcessing(false);
        return;
      }

      // Process the confirmations
      const result = await bulkConfirmReject(confirmations);
      setResults(result);
      
      if (result.succeeded > 0) {
        toast.success(`Successfully processed ${result.succeeded} of ${result.processed} confirmations`);
      } else {
        toast.error('No confirmations could be processed');
      }
    } catch (error: any) {
      console.error('Error processing file:', error);
      setValidationErrors([error.message || 'Error processing file']);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Bulk Confirm/Reject Paid Upgrades"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-medium text-blue-800 mb-2">Instructions</h4>
              <p className="text-sm text-blue-700">
                Upload an Excel file to process multiple paid upgrades at once. You can confirm or reject each upgrade.
              </p>
              <ul className="mt-2 list-disc list-inside text-sm text-blue-700">
                <li>Passport Number: The passport of the pilgrim</li>
                <li>Hotel Name: The hotel where the upgrade was requested</li>
                <li>Status: Either "confirm" or "reject"</li>
                <li>Reason: Optional rejection reason (only needed for rejected upgrades)</li>
              </ul>
              <p className="mt-2 text-sm text-blue-700 font-medium">
                Note: Only paid upgrades will be processed. Upgrades in other statuses will be ignored.
              </p>
            </div>
          </div>
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>Download Excel Template</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
            setResults(null);
          }}
        />

        <ValidationErrors errors={validationErrors} />

        {/* Results summary */}
        {results && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 p-4 rounded-lg"
          >
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <CheckCircle size={18} className="text-green-500" />
              Processing Results
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Processed:</span>
                <span className="font-medium">{results.processed}</span>
              </div>
              <div className="flex justify-between">
                <span>Successfully Processed:</span>
                <span className="font-medium text-green-600">{results.succeeded}</span>
              </div>
              <div className="flex justify-between">
                <span>Failed:</span>
                <span className="font-medium text-red-600">{results.failed}</span>
              </div>
            </div>

            {/* Error details */}
            {results.errors.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-red-500" />
                  <h4 className="font-medium text-red-600">Errors</h4>
                </div>
                <div className="max-h-40 overflow-y-auto bg-white p-2 rounded border border-red-100">
                  <table className="min-w-full text-xs">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Passport</th>
                        <th className="px-2 py-1 text-left">Hotel</th>
                        <th className="px-2 py-1 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {results.errors.map((error, index) => (
                        <tr key={index} className="hover:bg-red-50">
                          <td className="px-2 py-1">{error.passport}</td>
                          <td className="px-2 py-1">{error.hotel}</td>
                          <td className="px-2 py-1 text-red-600">{error.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
          <button
            onClick={handleUpload}
            disabled={isProcessing || !file}
            className={`px-6 py-2 rounded-lg text-white
              ${isProcessing || !file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isProcessing ? 'Processing...' : 'Process File'}
          </button>
        </div>
      </div>
    </Modal>
  );
};